import { Component, EventEmitter, HostBinding, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ContentService } from '../../services/content.service';
import { LangService } from '../../services/lang.service';
import { CrimeType } from '../../structure/types/crime-type.class';

@Component({
    selector: 'app-crime-type-picker',
    templateUrl: './crime-type-picker.component.html',
    styleUrls: ['./crime-type-picker.component.scss'],
    standalone: false,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CrimeTypePickerComponent),
        multi: true,
    }],
})
export class CrimeTypePickerComponent implements ControlValueAccessor, OnInit {

    /** Render the title. Off for hosts that supply their own heading — the modal
     *  labels this like any other form field. titleText still labels the group
     *  for screen readers either way. */
    @Input() showTitle = true;

    /** Overrides the default subtitle copy (the account page asks for a shorter line
     *  because it carries its own note above the save button). */
    @Input() subtitle: string | null = null;

    /** 'pills' on the full-page surfaces, 'checkboxes' in the modal. */
    @Input() variant: 'pills' | 'checkboxes' = 'pills';

    /** The modal reads the subtitle as a footnote under the options instead. */
    @Input() subtitlePosition: 'above' | 'below' = 'above';

    /** Let the options list take the leftover height and scroll on its own, so the
     *  host (the modal) never has to. Requires a flex-column parent chain. */
    @Input() scrollable = false;

    /** Tick the domestic-abuse pill once the list loads (DA referrals). The user
     *  is free to untick it — this is a starting point, not a lock. */
    @Input() preselectDomesticAbuse = false;

    /** Error styling, driven by the host's own error state. */
    @Input() invalid = false;

    @Output() selectionChange = new EventEmitter<number[]>();
    @Output() loaded = new EventEmitter<CrimeType[]>();

    crimeTypes: CrimeType[] = [];
    value: number[] = [];
    disabled = false;

    @HostBinding('class.is-scrollable')
    get isScrollable(): boolean {
        return this.scrollable;
    }

    private hasFormValue = false;
    private onChange: (value: number[]) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private contentService: ContentService, public lang: LangService) {}

    get titleText(): string {
        return this.lang.data['crime_types.title'] || 'Which areas can we support you in?';
    }

    get subtitleText(): string {
        return this.subtitle
            || this.lang.data['crime_types.subtitle']
            || 'Choose any of the options from below. You can amend this selection later in My Support Space app Settings.';
    }

    async ngOnInit() {
        this.crimeTypes = await this.contentService.getCrimeTypesData();
        this.loaded.emit(this.crimeTypes);

        // Only pre-select when the form supplied nothing, so an existing selection
        // is never overwritten.
        if (this.preselectDomesticAbuse && !this.hasFormValue && this.value.length === 0) {
            const domesticAbuse = this.crimeTypes.find(crimeType => !!crimeType.is_domestic_abuse);

            if (domesticAbuse) {
                this.value = [domesticAbuse.id];
                this.onChange(this.value);
                this.selectionChange.emit(this.value);
            }
        }
    }

    isSelected(id: number): boolean {
        return this.value.includes(id);
    }

    /**
     * "Prefer not to say" is mutually exclusive with every other option. Identified by the
     * backend flag (mirrors is_domestic_abuse), so it survives renames and translations.
     */
    private isPreferNotToSay(crimeType: CrimeType): boolean {
        return !!crimeType?.is_prefer_not_to_say;
    }

    /** Id of the "Prefer not to say" option, or null if the list doesn't contain it. */
    private get preferNotToSayId(): number | null {
        return this.crimeTypes.find(crimeType => this.isPreferNotToSay(crimeType))?.id ?? null;
    }

    /** True while "Prefer not to say" is the current selection. */
    private get preferNotToSayActive(): boolean {
        const id = this.preferNotToSayId;
        return id != null && this.isSelected(id);
    }

    /**
     * Options are locked when the host disables the whole control, and every *other* option
     * is locked while "Prefer not to say" is selected — it must stand alone.
     */
    isOptionDisabled(crimeType: CrimeType): boolean {
        if (this.disabled) {
            return true;
        }
        return this.preferNotToSayActive && crimeType.id !== this.preferNotToSayId;
    }

    toggle(id: number) {
        if (this.disabled) {
            return;
        }

        const pntsId = this.preferNotToSayId;

        // While "Prefer not to say" is selected, no other option is interactive — the only
        // allowed action is unticking it.
        if (this.preferNotToSayActive && id !== pntsId) {
            return;
        }

        const selecting = !this.isSelected(id);

        if (selecting && id === pntsId) {
            // Picking "Prefer not to say" clears everything else — it stands alone.
            this.value = [id];
        } else {
            this.value = selecting
                ? [...this.value, id]
                : this.value.filter(selected => selected !== id);
        }

        this.onChange(this.value);
        this.onTouched();
        this.selectionChange.emit(this.value);
    }

    writeValue(value: number[] | null): void {
        this.value = Array.isArray(value) ? [...value] : [];

        if (this.value.length) {
            this.hasFormValue = true;
        }
    }

    registerOnChange(fn: (value: number[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
